import { useLayoutEffect, useRef, useState } from 'react';
import { IconChevronDown } from '../../design-system/icons';

/**
 * Multi-waypoint picker for patrol's comma-separated places field
 * (plan §Phase 4E UX fix). A native `<input list=datalist>` only
 * matches the datalist against the FULL current input value — once a
 * user picked "lobby" the field became "lobby, " and no option
 * starts with that string, so the dropdown silently stopped showing
 * suggestions for every waypoint after the first. This renders
 * selected waypoints as removable chips and runs its own filtered
 * dropdown against just the text typed since the last chip, so
 * picking the next waypoint works the same way every time.
 *
 * Storage stays a comma-joined string (`value`/`onChange`) so the
 * rest of the form — `buildPhases`, CSV export, validation — is
 * untouched; this component only changes how that string gets typed.
 */
/** Gap between the control and the list, matching the CSS offset. */
const GAP = 4;
/** Below this the list is not worth opening downward — flip instead. */
const MIN_USABLE_HEIGHT = 120;
const DEFAULT_MAX_HEIGHT = 220;

export function WaypointMultiSelect({
  value,
  onChange,
  options,
  placeholder,
  testId,
}: {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  testId?: string;
}) {
  const [draft, setDraft] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  // Where the list opens, and how tall it may be. Both are measured
  // rather than fixed -- see `placeDropdown`.
  const [place, setPlace] = useState<{ up: boolean; maxHeight: number }>({
    up: false,
    maxHeight: DEFAULT_MAX_HEIGHT,
  });

  const selected = value
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  const filtered =
    draft.trim().length === 0
      ? options
      : options.filter((o) => o.toLowerCase().includes(draft.trim().toLowerCase()));

  /**
   * Open upward when opening downward would put the list under
   * something.
   *
   * The waypoint field sits directly above the dispatch form's sticky
   * footer, so a fixed `top: 100%` list ran straight into it. The
   * footer does paint above the list (z-index 30 vs 20, measured) --
   * but that only means the options are HIDDEN behind it rather than
   * overlapping it, which is no better: the operator sees a list they
   * cannot scroll to the end of, sitting across the dispatch button.
   *
   * The obstruction is found by geometry, not by class name. Naming
   * `.nx-task-form__footer` here would couple a generic control to one
   * of its callers and would silently stop working for the next sticky
   * bar someone adds. Any sticky/fixed element that overlaps this
   * control horizontally and starts at or below its bottom edge is a
   * lid; the highest such lid wins. A sticky ancestor is excluded by
   * that same rule rather than by a special case, since a panel
   * framing this control always starts above it.
   */
  function placeDropdown() {
    const host = hostRef.current;
    if (!host) return;
    const r = host.getBoundingClientRect();

    // The band this list is allowed to paint in. Starts as the viewport
    // and is narrowed by every scrolling/clipping ancestor, because a
    // list drawn outside one is not "overhanging" -- it is SHEARED, and
    // the part that goes missing when opening upward is the FIRST
    // option. Measuring against the viewport alone is what produced
    // exactly that: the shell's content pane starts 67px down, the list
    // was placed from 41px, and the top 26px was invisible.
    let clipTop = 0;
    let clipBottom = window.innerHeight;
    for (let el = host.parentElement; el; el = el.parentElement) {
      const cs = getComputedStyle(el);
      if (!/auto|scroll|hidden/.test(cs.overflowY + cs.overflowX + cs.overflow)) continue;
      const b = el.getBoundingClientRect();
      if (b.height === 0) continue;
      clipTop = Math.max(clipTop, b.top);
      clipBottom = Math.min(clipBottom, b.bottom);
    }

    // A lid is anything sticky/fixed that overlaps this control
    // horizontally and starts at or below its bottom edge -- the
    // dispatch form's sticky footer, and whatever sticky bar someone
    // adds next. Found by geometry rather than by class name so the
    // control stays independent of its callers. A sticky ANCESTOR falls
    // out of the same rule, since a panel framing this control always
    // starts above it.
    let lid = clipBottom;
    for (const el of document.querySelectorAll<HTMLElement>('body *')) {
      const pos = getComputedStyle(el).position;
      if (pos !== 'sticky' && pos !== 'fixed') continue;
      const b = el.getBoundingClientRect();
      if (b.height === 0 || b.top < r.bottom) continue;
      if (b.right <= r.left || b.left >= r.right) continue;
      lid = Math.min(lid, b.top);
    }

    const below = lid - r.bottom - GAP;
    const above = r.top - clipTop - GAP;
    // Only flip when down genuinely cannot show a usable list AND up is
    // roomier -- flipping for a marginal gain just moves the surprise.
    const up = below < MIN_USABLE_HEIGHT && above > below;
    const room = up ? above : below;
    setPlace({
      up,
      // Never exceed the room actually measured, even when that is less
      // than MIN_USABLE_HEIGHT: overhanging by a few pixels is a
      // cosmetic flaw, but exceeding `room` upward hides the option the
      // user is most likely to want.
      maxHeight: Math.max(0, Math.min(DEFAULT_MAX_HEIGHT, room)),
    });
  }

  // Layout effect, not an effect: this runs before paint, so the list
  // never shows for a frame in the wrong place.
  useLayoutEffect(() => {
    if (open) placeDropdown();
    // `filtered.length` is a dependency because the list's height
    // changes as the user types, which changes whether it fits.
  }, [open, filtered.length]);

  function commit(next: string[]) {
    onChange(next.join(', '));
  }

  function addWaypoint(name: string) {
    const trimmed = name.trim();
    if (!trimmed) return;
    commit([...selected, trimmed]);
    setDraft('');
    setHighlight(0);
    // Keep focus + dropdown open so the next waypoint can be added
    // immediately — this is the whole point of the fix.
    inputRef.current?.focus();
  }

  function removeAt(index: number) {
    commit(selected.filter((_, i) => i !== index));
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      const pick = filtered[Math.min(highlight, filtered.length - 1)];
      if (open && pick && draft.trim().length > 0) {
        addWaypoint(pick);
      } else if (draft.trim().length > 0) {
        addWaypoint(draft);
      }
      return;
    }
    if (e.key === 'Backspace' && draft.length === 0 && selected.length > 0) {
      removeAt(selected.length - 1);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.min(h + 1, Math.max(filtered.length - 1, 0)));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
      return;
    }
    if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div className="nx-waypoint-select" ref={hostRef}>
      <div className="nx-waypoint-select__chips">
        {selected.map((name, i) => (
          <span className="nx-waypoint-select__chip" key={`${name}-${i}`}>
            {name}
            <button
              type="button"
              className="nx-waypoint-select__chip-remove"
              aria-label={`Remove ${name}`}
              onClick={() => removeAt(i)}
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          className="nx-waypoint-select__input"
          value={draft}
          placeholder={selected.length === 0 ? placeholder : 'Add another…'}
          onChange={(e) => {
            setDraft(e.target.value);
            setHighlight(0);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 120)}
          onKeyDown={onKeyDown}
          data-testid={testId}
        />
        {options.length > 0 && (
          <button
            type="button"
            className="nx-waypoint-select__toggle"
            aria-label={open ? 'Hide suggestions' : 'Show suggestions'}
            aria-expanded={open}
            // mousedown (not click), same reason as the option buttons —
            // fires before the input's onBlur would otherwise race it.
            onMouseDown={(e) => {
              e.preventDefault();
              setOpen((v) => !v);
              if (!open) inputRef.current?.focus();
            }}
          >
            <IconChevronDown
              size={14}
              className={`nx-waypoint-select__toggle-icon${open ? ' nx-waypoint-select__toggle-icon--open' : ''}`}
            />
          </button>
        )}
      </div>
      {open && options.length > 0 && (
        <ul
          className={`nx-waypoint-select__dropdown${place.up ? ' nx-waypoint-select__dropdown--up' : ''}`}
          style={{ maxHeight: place.maxHeight }}
          role="listbox"
        >
          {filtered.length === 0 ? (
            <li className="nx-waypoint-select__dropdown-empty">No matching places</li>
          ) : (
            filtered.map((name, i) => (
              <li key={name}>
                <button
                  type="button"
                  role="option"
                  aria-selected={i === highlight}
                  className={`nx-waypoint-select__option${i === highlight ? ' nx-waypoint-select__option--highlight' : ''}`}
                  // mousedown (not click) fires before the input's
                  // onBlur closes the dropdown.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    addWaypoint(name);
                  }}
                >
                  {name}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
